import React, { useState, useEffect } from 'react';
import { RefreshCw, Download, X } from 'lucide-react';
import { Pill } from '../ui/pill';
import { Card, CardContent } from '../ui/card';
import { useTranslation } from '../../locales';
import { useLanguageStore } from '../../stores/languageStore';

interface UpdateNotificationProps {
  onUpdate: () => void;
  onDismiss: () => void;
  isVisible: boolean;
}

export const UpdateNotification: React.FC<UpdateNotificationProps> = ({
  onUpdate,
  onDismiss,
  isVisible
}) => {
  const { currentLanguage } = useLanguageStore();
  const t = useTranslation(currentLanguage);
  const [isUpdating, setIsUpdating] = useState(false);

  const handleUpdate = async () => {
    setIsUpdating(true);
    try {
      await onUpdate();
    } finally {
      setIsUpdating(false);
    }
  };

  if (!isVisible) return null;

  return (
    <div className="fixed top-4 right-4 z-50 max-w-sm">
      <GlassyCard className="shadow-lg border-l-4 border-l-blue-500 bg-card dark:bg-card">
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-indigo-500/20 bg-indigo-500/20 rounded-full flex items-center justify-center">
                  <Download className="w-5 h-5 text-indigo-400 text-indigo-400" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-foreground text-foreground">
                  {t.pwaUpdate.title}
                </h3>
                <p className="text-xs text-muted-foreground dark:text-foreground mt-1">
                  {t.pwaUpdate.newVersionAvailable}
                </p>
              </div>
            </div>
            <button
              onClick={onDismiss}
              className="flex-shrink-0 ml-2 text-muted-foreground hover:text-muted-foreground dark:hover:text-foreground"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          
          <div className="mt-4 flex space-x-2">
            <Pill
              onClick={handleUpdate}
              disabled={isUpdating}
              size="sm"
              variant="primary"
              className="flex-1"
            >
              {isUpdating ? (
                <>
                  <RefreshCw className="w-3 h-3 mr-2 animate-spin" />
                  {t.pwaUpdate.updating}
                </>
              ) : (
                <>
                  <Download className="w-3 h-3 mr-2" />
                  {t.pwaUpdate.updateNow}
                </>
              )}
            </Pill>
            <Pill
              onClick={onDismiss}
              variant="secondary"
              size="sm"
            >
              {t.pwaUpdate.later}
            </Pill>
          </div>
        </CardContent>
      </GlassyCard>
    </div>
  );
};

export default UpdateNotification; 